import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import CieloTDCProvider from 'components/cielo/CieloTDCContext/CieloTDCProvider';
import useCieloTDCContext from 'components/cielo/CieloTDCContext/useCieloTDCContext';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { selectPaymentMethod, getPurchase } from '@/actions/purchase';
import CieloWebViewForm from './CieloWebViewForm';
import 'styles/components/secure/CieloWebView3DS';

/**
 * Payment and purchase status constants
 */
const STATUS = {
  COMPLETED: 'completed',
  CHARGED: 'charged',
  PENDING: 'pending',
  ATTEMPT: 'attempt',
  FAILED: 'failed',
  REJECTED: 'rejected',
};

/**
 * Unified WebView form component wrapper for Cielo payments.
 * This component handles BOTH 3DS and SOP flows automatically based on configuration.
 * It replicates the same behavior as CieloTDCForm but adapted for WebView usage.
 *
 * The component handles:
 * - 3DS authentication (bpmpi_authenticate)
 * - Silent Order Post tokenization (window.bpSop_silentOrderPost)
 * - Both flows can work simultaneously if both are enabled
 * - CieloTDCProvider manages which scripts to load based on config
 *
 * Flow:
 * 1. User fills the form and submits
 * 2. Form triggers submitForm() from CieloTDCContext
 * 3. CieloTDCProvider's onSubmit handles:
 *    - If 3DS enabled: calls bpmpi_authenticate() → onSuccess callback
 *    - If SOP enabled: calls window.bpSop_silentOrderPost() → onSuccess callback
 * 4. Success callbacks dispatch cardPaymentAttempt with appropriate data
 * 5. Monitor payment state and notify WebView of results
 */
const CieloWebViewFormWrapper = ({ purchaseData }) => {
  const { t } = useTranslation(['payment', 'common', 'errors']);
  const { form, hasError, mountCieloTDCForm, config, isRetryWith3DS } = useCieloTDCContext();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Monitor payment state to notify WebView of results
  const payment = useSelector((state) => state.payment);
  const purchase = useSelector((state) => state.purchase);
  const paymentEngine = useSelector((state) => state.purchase.get('paymentEngine'));
  const [hasNotifiedResult, setHasNotifiedResult] = useState(false);

  // Use ref to prevent multiple form mounts
  const formMountedRef = useRef(false);

  // Keep Cielo as the selected payment method whenever it gets cleared.
  // getPurchase (called on mount and during payment polling) internally dispatches
  // selectPaymentMethod with whatever matches defaultPaymentMethod; since Cielo
  // is WebView-only it never matches, leaving paymentEngine = null. Without this
  // guard the 3DS retry's cardPaymentAttempt throws "Card strategy not set".
  useEffect(() => {
    if (paymentEngine !== 'cielo') {
      dispatch(
        selectPaymentMethod({
          type: 'credit_card',
          engine: 'cielo',
          provider: 'cielo',
          extra: {},
        }),
      );
    }
  }, [paymentEngine, dispatch]);

  // Initialize Cielo form when component mounts - only once
  // CieloTDCProvider will load the appropriate scripts (3DS and/or SOP) based on config
  useEffect(() => {
    if (formMountedRef.current) {
      return;
    }
    formMountedRef.current = true;
    mountCieloTDCForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize purchaseData to prevent unnecessary re-renders
  const memoizedPurchaseData = useMemo(
    () => purchaseData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [purchaseData.token, purchaseData.total],
  );

  // Monitor payment state and notify WebView of results
  useEffect(() => {
    const paymentState = payment?.toJS ? payment.toJS() : payment;
    const purchaseState = purchase?.toJS ? purchase.toJS() : purchase;

    const purchaseStatus = purchaseState?.status;
    const paymentStatus = paymentState?.status;
    const hasError = Boolean(paymentState?.error);
    const suppressErrors = isRetryWith3DS && paymentStatus === STATUS.ATTEMPT;

    // Check for successful states first
    const isPaymentSuccessful =
      !hasError &&
      ((purchaseStatus === STATUS.COMPLETED && paymentStatus === STATUS.CHARGED) ||
        (purchaseStatus === STATUS.COMPLETED && paymentStatus === STATUS.COMPLETED) ||
        (purchaseStatus === STATUS.CHARGED && paymentStatus === STATUS.CHARGED) ||
        paymentStatus === STATUS.CHARGED);

    const isPaymentPending =
      purchaseStatus === STATUS.PENDING ||
      paymentStatus === STATUS.PENDING ||
      (purchaseStatus === STATUS.ATTEMPT && paymentStatus === STATUS.PENDING);

    if (isPaymentSuccessful) {
      setHasNotifiedResult(true);
      // Redirect to home so parent app can detect WebView exit and close it
      history.push('/');
      return;
    }

    // Check if payment is pending (needs manual verification or processing)
    if (isPaymentPending) {
      setHasNotifiedResult(true);
      // Redirect to home so parent app can detect WebView exit and close it
      history.push('/');
      return;
    }

    // For errors, show message and stay in WebView for retry
    // Don't notify WebView of errors during 3DS retry flow
    const isFailedOrRejected =
      purchaseStatus === STATUS.FAILED || purchaseStatus === STATUS.REJECTED;
    if (!hasNotifiedResult && (hasError || isFailedOrRejected) && !suppressErrors) {
      setIsSubmitting(false);
      setHasNotifiedResult(true);

      // Send error message to parent WebView for user notification
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'PAYMENT_ERROR',
            error: paymentState?.error || 'Payment failed',
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, purchase, history, memoizedPurchaseData?.token, isRetryWith3DS]);

  // Enhance 3DS modal visibility for WebView (only if 3DS is enabled)
  useEffect(() => {
    if (!config.enableThreeDS) {
      return;
    }

    /**
     * Enhances the visibility of 3DS modal for WebView.
     * Applies dark backdrop and styling to modal elements.
     */
    const enhanceModalVisibility = () => {
      const attempts = [100, 500, 1000, 2000];

      attempts.forEach((delay) => {
        setTimeout(() => {
          const allElements = document.querySelectorAll('*');

          allElements.forEach((element) => {
            const computedStyle = window.getComputedStyle(element);
            const zIndex = parseInt(computedStyle.zIndex, 10) || 0;

            if (
              computedStyle.position === 'fixed' &&
              zIndex > 999 &&
              (element.offsetWidth > 200 || element.offsetHeight > 200)
            ) {
              element.style.setProperty('background-color', 'rgba(0, 0, 0, 0.8)', 'important');
              element.style.setProperty('backdrop-filter', 'blur(4px)', 'important');
              element.style.setProperty('-webkit-backdrop-filter', 'blur(4px)', 'important');

              const iframes = element.querySelectorAll('iframe');
              iframes.forEach((iframe) => {
                const overlay = document.createElement('div');
                overlay.style.position = 'fixed';
                overlay.style.top = '0';
                overlay.style.left = '0';
                overlay.style.width = '100vw';
                overlay.style.height = '100vh';
                overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                overlay.style.zIndex = (zIndex - 1).toString();
                overlay.style.pointerEvents = 'none';

                if (
                  element.parentNode &&
                  !element.parentNode.querySelector('.webview-modal-overlay')
                ) {
                  overlay.className = 'webview-modal-overlay';
                  element.parentNode.insertBefore(overlay, element);
                }

                if (iframe.parentElement) {
                  iframe.parentElement.style.setProperty(
                    'background-color',
                    '#ffffff',
                    'important',
                  );
                  iframe.parentElement.style.setProperty('border-radius', '12px', 'important');
                  iframe.parentElement.style.setProperty(
                    'box-shadow',
                    '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    'important',
                  );
                  iframe.parentElement.style.setProperty(
                    'border',
                    '2px solid rgba(255, 255, 255, 0.9)',
                    'important',
                  );
                }
              });
            }
          });
        }, delay);
      });
    };

    const observer = new MutationObserver((mutations) => {
      let hasNewNodes = false;

      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              hasNewNodes = true;
            }
          });
        }
      });

      if (hasNewNodes) {
        enhanceModalVisibility();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    enhanceModalVisibility();

    return () => observer.disconnect();
  }, [config.enableThreeDS]);

  /**
   * Handles form submission for Cielo payment.
   * The CieloTDCProvider's submitForm will handle both 3DS and SOP flows
   * based on what's enabled in the configuration.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard against double-submits
    if (isSubmitting) return;

    setIsSubmitting(true);
    setHasNotifiedResult(false);

    try {
      // Validate form first
      const errors = await form.validateForm();
      if (Object.keys(errors).length > 0) {
        form.setTouched(
          Object.keys(errors).reduce((touched, key) => {
            touched[key] = true;
            return touched;
          }, {}),
        );
        setIsSubmitting(false);
        return;
      }

      // Submit form - CieloTDCProvider will handle the appropriate flow (3DS or SOP)
      await form.submitForm();

      // Trigger modal enhancement for 3DS if enabled
      if (config.enableThreeDS) {
        setTimeout(() => {
          /**
           * Enhances modal styling after form submission for 3DS flow.
           * Applies backdrop and blur effects to high z-index elements.
           */
          const enhanceModalAfterSubmit = () => {
            const allElements = document.querySelectorAll('*');
            allElements.forEach((element) => {
              const style = window.getComputedStyle(element);
              const zIndex = parseInt(style.zIndex, 10) || 0;

              if (style.position === 'fixed' && zIndex > 999) {
                element.style.setProperty('background-color', 'rgba(0, 0, 0, 0.8)', 'important');
                element.style.setProperty('backdrop-filter', 'blur(4px)', 'important');
                element.style.setProperty('-webkit-backdrop-filter', 'blur(4px)', 'important');
              }
            });
          };

          enhanceModalAfterSubmit();
          setTimeout(enhanceModalAfterSubmit, 500);
        }, 100);
      }

      // Note: The actual payment will be triggered in the onSuccess callback
      // of either the 3DS authentication or SOP tokenization process
    } catch (error) {
      // Send message to parent WebView
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: 'PAYMENT_ERROR',
            error: error.message || 'Payment processing failed',
          }),
        );
      }
      setIsSubmitting(false);
    }
  };

  /**
   * Handles payment cancellation.
   */
  const handleCancel = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: 'PAYMENT_CANCELLED',
        }),
      );
    } else {
      history.goBack();
    }
  };

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg p-6 text-center">
          <div className="text-red-500 text-2xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('errors:error')}</h2>
          <p className="text-gray-600 mb-4">{t('cielo.error_loading')}</p>
          <button
            type="button"
            onClick={handleCancel}
            className="webview-action-button w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
          >
            {t('errors:cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-6 text-center">
            <span className="security-icon">🔒</span>
            <span>{t('secure_payment')}</span>
          </h1>

          <form onSubmit={handleSubmit} className="webview-3ds-form">
            <CieloWebViewForm purchaseData={purchaseData} />

            <div className="mt-6 flex flex-col gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="webview-action-button w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                {isSubmitting ? t('processing') : t('continue_payment')}
              </button>

              <button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="webview-action-button w-full bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded transition-colors duration-200"
              >
                {t('errors:cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

CieloWebViewFormWrapper.propTypes = {
  purchaseData: PropTypes.shape({
    token: PropTypes.string,
    total: PropTypes.number,
  }).isRequired,
};

/**
 * Unified WebView component for Cielo payments.
 * Automatically handles both 3DS and Silent Order Post based on configuration.
 * This matches the behavior of the regular CieloTDCForm in the checkout flow.
 *
 * Unlike the previous separate components (CieloWebView3DS and CieloWebViewSOP),
 * this unified component allows both flows to coexist and be selected automatically
 * by CieloTDCProvider based on environment configuration.
 */
const CieloWebView = () => {
  const { token } = useParams();
  const { t } = useTranslation('payment');
  const env = useWhitelabelEnvs();
  const dispatch = useDispatch();

  // Extract purchase data from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const purchaseToken = token || urlParams.get('token');

  // Fetch purchase data into Redux so total/installments are available for 3DS.
  // getPurchase clobbers selectedPaymentMethod with defaultPaymentMethod — the
  // reactive guard in CieloWebViewFormWrapper restores Cielo after each clobber.
  useEffect(() => {
    if (purchaseToken) {
      dispatch(getPurchase(purchaseToken));
    }
  }, [dispatch, purchaseToken]);

  // Memoize purchaseData to prevent unnecessary re-renders.
  // The installment plan is deliberately NOT read from the URL: the selector writes it to
  // purchase.monthlySelectedPlan, which is the single value both the 3DS bpmpi_installments field
  // and the created payment read. A URL-supplied plan only ever reached the first of the two, so it
  // could authenticate one plan and charge another.
  const purchaseData = useMemo(
    () => ({
      token: purchaseToken,
      total: parseFloat(urlParams.get('total')) || 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [purchaseToken, urlParams.get('total')],
  );

  // Validate that Cielo is enabled
  if (!env.cielo?.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg p-6 text-center">
          <div className="text-yellow-500 text-2xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('service_unavailable')}</h2>
          <p className="text-gray-600">{t('cielo_not_enabled')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app webview-container">
      <Helmet>
        <title>{t('secure_payment')} - Cielo</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <CieloTDCProvider config={env.cielo} purchaseToken={purchaseData.token}>
        <CieloWebViewFormWrapper purchaseData={purchaseData} />
      </CieloTDCProvider>
    </div>
  );
};

export default CieloWebView;
